import { z } from 'zod';
import type { LanguageCode } from '../lang.js';
import { EvidenceSchema, RecommendationSchema } from '../scoring/assessment.js';

/**
 * The skills assessment (stage 2): after a recruiter shortlists a candidate, the AI runs a video
 * meeting of practical tasks. Each job keeps a bank of tasks; every candidate gets a different
 * selection (planAssessment), with customer role-plays in the languages they speak.
 */

const key = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/, 'lower_snake_case, 2-41 characters');
const text = (max: number) => z.string().trim().min(3).max(max);

/** The AI plays the other person (a customer, client, patient, colleague...); the candidate handles the conversation. */
export const RolePlayTaskSchema = z.object({
  kind: z.literal('roleplay'),
  id: key,
  title: text(120),
  /** What the candidate is told before it starts ({customer}, {org}, {school}, {city} and {company} are filled in). */
  brief: text(600),
  /** Who the AI is: role, mood, how they talk. */
  persona: text(600),
  /** The facts of the problem, including a hidden cause a good agent uncovers by asking. */
  situation: text(1200),
  /** An escalation the AI brings in part-way through. */
  twist: text(600).optional(),
  /** What a strong candidate does: the scorer's yardstick. */
  goodResolution: text(1200),
  /** Competency keys this task gives evidence for. */
  skills: z.array(key).max(6).default([]),
  minutes: z.number().int().min(2).max(10).default(4),
});

/** The candidate types an answer in the meeting page. */
export const WrittenTaskSchema = z.object({
  kind: z.literal('written'),
  id: key,
  title: text(120),
  /** Shown on the candidate's screen: the situation and exactly what to write. */
  brief: text(2000),
  goodAnswer: text(1200),
  skills: z.array(key).max(6).default([]),
  minutes: z.number().int().min(2).max(15).default(5),
});

/** The candidate shares their whole screen and does something in a demo system. */
export const HandsOnTaskSchema = z.object({
  kind: z.literal('hands_on'),
  id: key,
  title: text(120),
  /** Which demo system (DemoSystem.system), e.g. erp, crm, tally. */
  system: key,
  /** The steps, shown on the candidate's screen with the demo login. */
  brief: text(2000),
  /** What should be visible or done at the end: the scorer's and the AI's yardstick. */
  goodAnswer: text(1200),
  skills: z.array(key).max(6).default([]),
  minutes: z.number().int().min(3).max(20).default(7),
});

export const AssessmentTaskSchema = z.discriminatedUnion('kind', [RolePlayTaskSchema, WrittenTaskSchema, HandsOnTaskSchema]);
export type AssessmentTask = z.infer<typeof AssessmentTaskSchema>;
export type RolePlayTask = z.infer<typeof RolePlayTaskSchema>;

/** Demo access for hands-on tasks; shown to the candidate during that task only. */
export const DemoSystemSchema = z.object({
  system: key,
  /** The name the candidate and the AI use ("ERP", "Tally", "Salesforce"); the key in capitals when empty. */
  label: z.string().trim().max(40).optional(),
  url: z.string().url(),
  username: z.string().min(1).max(200),
  password: z.string().min(1).max(200),
  notes: z.string().max(500).optional(),
});
export type DemoSystem = z.infer<typeof DemoSystemSchema>;

export const AssessmentConfigSchema = z
  .object({
    /** Role-plays beyond the English one, in other languages the candidate speaks (not their mother tongue). */
    extraLanguages: z.number().int().min(0).max(3).default(2),
    /** At least this many customer role-plays: one per role-play language, the rest in English. */
    rolePlays: z.number().int().min(1).max(6).default(3),
    /** Include one written task. */
    written: z.boolean().default(true),
    /**
     * Who runs the meeting. standard (the owner's choice since 2026-10-08): Sarvam hears and speaks, Gemini
     * Flash follows the task script (~2 s replies). gemini-live: one model hears and speaks (~1 s replies);
     * the agent sends it each task as a platform message (agent.py brief). It was the choice from
     * 2026-09-29 until its skipped turns and its cost on real calls ended the trial; it is the only one
     * that can watch the shared screen in a hands-on task.
     */
    voiceEngine: z.enum(['standard', 'gemini-live']).default('standard'),
    /** Hands-on tasks run only for systems listed here (with demo access). */
    systems: z.array(DemoSystemSchema).max(4).default([]),
    /** Client organisations the tasks mention as {org}; a built-in list of Indian business names when empty. */
    orgs: z.array(z.string().trim().min(2).max(80)).max(30).optional(),
    tasks: z.array(AssessmentTaskSchema).min(1).max(80),
  })
  .superRefine((cfg, ctx) => {
    const seen = new Set<string>();
    cfg.tasks.forEach((t, i) => {
      if (seen.has(t.id)) ctx.addIssue({ code: 'custom', path: ['tasks', i, 'id'], message: `Duplicate task id ${t.id}` });
      seen.add(t.id);
    });
    if (!cfg.tasks.some((t) => t.kind === 'roleplay')) ctx.addIssue({ code: 'custom', path: ['tasks'], message: 'Add at least one role-play' });
  });
export type AssessmentConfig = z.infer<typeof AssessmentConfigSchema>;

/** One task as chosen for one candidate: placeholders filled, language fixed. Stored on the interview. */
export interface PlannedTask {
  n: number;
  taskId: string;
  kind: AssessmentTask['kind'];
  title: string;
  brief: string;
  language: LanguageCode;
  minutes: number;
  persona?: string;
  situation?: string;
  twist?: string;
  /** roleplay: goodResolution; written / hands_on: goodAnswer. */
  yardstick: string;
  skills: string[];
  system?: string;
  /** The demo system's name as the candidate sees it. */
  systemLabel?: string;
}

export interface AssessmentPlan {
  tasks: PlannedTask[];
  languages: LanguageCode[];
  minutes: number;
}

/** What the agent records per task on the interview (state.tasks). */
export interface TaskLog {
  taskId: string;
  n: number;
  startSeq?: number;
  endSeq?: number;
  startedAt?: string;
  endedAt?: string;
  endReason?: 'done' | 'time' | 'skipped';
  written?: string;
  notes: string[];
  /** Integrity events the page reported during this task (tab switches, leaving fullscreen, pastes). */
  integrity?: Record<string, number>;
}

// ---- The scorer's output --------------------------------------------------------------

export const TaskResultSchema = z.object({
  taskId: z.string(),
  /** 1-5, null when there is too little evidence. */
  score: z.number().int().nullable(),
  /** Role-plays: how well they spoke the task's language, 1-5; null otherwise. */
  languageFluency: z.number().int().nullable(),
  evidence: z.array(EvidenceSchema),
  rationale: z.string(),
});
export type TaskResult = z.infer<typeof TaskResultSchema>;

export const SkillsAssessmentSchema = z.object({
  tasks: z.array(TaskResultSchema),
  summary: z.string(),
  strengths: z.array(z.string()),
  concerns: z.array(z.string()),
  /** Integrity events a reviewer should look at (flags, not verdicts). */
  integrityNotes: z.array(z.string()),
  recommendation: RecommendationSchema,
});
export type SkillsAssessment = z.infer<typeof SkillsAssessmentSchema>;

export const ASSESSMENT_TOOL_NAME = 'assessment_note';

/** The assessor's one tool: fixed for the whole session (Gemini Live cannot change tools mid-call). */
export function assessmentTool() {
  return {
    name: ASSESSMENT_TOOL_NAME,
    description:
      'Silent bookkeeping for the skills assessment; the candidate never sees it. event "ready": the candidate is ready to start. ' +
      'event "done": the current task has reached its natural end. event "note": something a reviewer should know about the current task.',
    input_schema: {
      type: 'object',
      properties: {
        event: { type: 'string', enum: ['ready', 'done', 'note'] },
        note: { type: 'string', description: 'For "note": one short factual sentence. Otherwise empty.' },
      },
      required: ['event'],
      additionalProperties: false,
    },
  } as const;
}

/** A demo system's display name. */
export function systemLabel(system: { system: string; label?: string | undefined }): string {
  return system.label?.trim() || system.system.replace(/_/g, ' ').toUpperCase();
}

export const TASK_KIND_LABEL: Record<PlannedTask['kind'], string> = {
  roleplay: 'Role-play',
  written: 'Written task',
  hands_on: 'Hands-on task',
};
